import React from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import { Modal, Button } from './index';

interface SessionExpiryModalProps {
  isOpen: boolean;
  onSignIn: () => void;
  autoRedirectSeconds?: number;
}

const SessionExpiryModal: React.FC<SessionExpiryModalProps> = ({
  isOpen,
  onSignIn,
  autoRedirectSeconds = 10
}) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const [countdown, setCountdown] = React.useState(autoRedirectSeconds);

  // Countdown effect
  React.useEffect(() => {
    if (!isOpen) {
      setCountdown(autoRedirectSeconds);
      return;
    }

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Auto redirect when countdown reaches 0
      onSignIn();
    }
  }, [isOpen, countdown, autoRedirectSeconds, onSignIn]);

  // Reset countdown when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCountdown(autoRedirectSeconds);
    }
  }, [isOpen, autoRedirectSeconds]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Prevent manual close - user must sign in
      title={t('common.sessionExpiryModal.title')}
    >
      <div className={cn('text-center py-8 px-6', tc.bg.surface)}>
        {/* Warning Icon */}
        <div className="text-6xl mb-6">⏰</div>

        {/* Title */}
        <h2 className={cn('text-2xl font-bold mb-4', tc.text.primary, tc.font.primary)}>
          {t('common.sessionExpiryModal.heading')}
        </h2>

        {/* Message */}
        <p className={cn('text-lg mb-6', tc.text.secondary, tc.font.primary)}>
          {t('common.sessionExpiryModal.message')}
        </p>

        {/* Countdown Notice */}
        {countdown > 0 && (
          <div className={cn(
            'sharp-corners border-l-4 p-4 mb-8',
            tc.border.primary,
            'bg-gray-50 dark:bg-gray-900/20'
          )}>
            <p className={cn('text-sm font-medium', tc.text.primary, tc.font.primary)}>
              {countdown === 1
                ? t('common.sessionExpiryModal.redirectingSingular', { countdown })
                : t('common.sessionExpiryModal.redirectingPlural', { countdown })}
            </p>
          </div>
        )}

        {/* Action Button */}
        <Button
          variant="primary"
          onClick={onSignIn}
          className={cn(
            'min-w-[200px] text-lg font-semibold py-3 px-6',
            tc.button.primary,
            'hover:scale-105 transition-transform duration-200'
          )}
        >
          {countdown > 0 ? t('common.sessionExpiryModal.signInNow') : t('common.sessionExpiryModal.redirecting')}
        </Button>

        {/* Additional Info */}
        <p className={cn('text-xs mt-6', tc.text.secondary, tc.font.primary)}>
          {t('common.sessionExpiryModal.helpText')}
        </p>
      </div>
    </Modal>
  );
};

export default SessionExpiryModal;